const OPS_IMAGE_PROXY_ORIGIN = 'https://images.belleandkate.com'

const DIGITALOCEAN_ORIGINS = [
  'https://belleandkate-production.sgp1.cdn.digitaloceanspaces.com',
  'https://belleandkate-production.sgp1.digitaloceanspaces.com'
]

const IMAGE_EXTENSION_PATTERN =
  /\.(?:avif|bmp|gif|jpe?g|png|svg|webp)$/i

const ANY_EXTENSION_PATTERN =
  /\.[a-z0-9]{2,5}$/i

const IMAGE_KEY_PATTERN =
  /(image|photo|receipt|proof|thumbnail|logo|stamp|card|cover)/i

const PRESIGNED_UPLOAD_KEY_PATTERN =
  /^(?:upload_url|presigned_upload_url|put_url)$/i

const PRESIGN_REQUEST_PATTERN =
  /(?:^|\/)presign(?:ed)?(?:$|[/?#])/i

const proxyEnabled =
  process.env.REACT_APP_OPS_IMAGE_PROXY_ENABLED === 'true'

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === '[object Object]'

const findDigitalOceanOrigin = (url) =>
  DIGITALOCEAN_ORIGINS.find(
    (origin) =>
      url === origin ||
      url.startsWith(`${origin}/`) ||
      url.startsWith(`${origin}?`)
  )

const isImageUrl = (url, key = '') => {
  try {
    const parsed = new URL(url)

    const responseContentType =
      parsed.searchParams.get('response-content-type') || ''

    if (responseContentType.toLowerCase().startsWith('image/')) {
      return true
    }

    if (IMAGE_EXTENSION_PATTERN.test(parsed.pathname)) {
      return true
    }

    // A known non-image extension (PDF, ZIP, etc.) must never be
    // treated as an image just because the field name contains "proof".
    if (ANY_EXTENSION_PATTERN.test(parsed.pathname)) {
      return false
    }

    // Handles opaque ActiveStorage / DigitalOcean object keys.
    return IMAGE_KEY_PATTERN.test(key)
  } catch (_error) {
    return false
  }
}

export const normalizeOpsImageUrl = (url, key = '') => {
  if (!proxyEnabled || !url || typeof url !== 'string') {
    return url
  }

  if (
    url === OPS_IMAGE_PROXY_ORIGIN ||
    url.startsWith(`${OPS_IMAGE_PROXY_ORIGIN}/`) ||
    url.startsWith(`${OPS_IMAGE_PROXY_ORIGIN}?`)
  ) {
    return url
  }

  // Upload URLs are intentionally left untouched.
  if (PRESIGNED_UPLOAD_KEY_PATTERN.test(key)) {
    return url
  }

  const matchedOrigin = findDigitalOceanOrigin(url)

  if (!matchedOrigin || !isImageUrl(url, key)) {
    return url
  }

  // Replace origin only.
  // Path and X-Amz signed query string remain byte-for-byte unchanged.
  return `${OPS_IMAGE_PROXY_ORIGIN}${url.slice(matchedOrigin.length)}`
}

export const normalizeOpsImageData = (value, key = '') => {
  if (!proxyEnabled || value == null) {
    return value
  }

  if (typeof value === 'string') {
    return normalizeOpsImageUrl(value, key)
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeOpsImageData(item, key))
  }

  if (!isPlainObject(value)) {
    return value
  }

  return Object.fromEntries(
    Object.entries(value).map(([childKey, childValue]) => [
      childKey,
      PRESIGNED_UPLOAD_KEY_PATTERN.test(childKey)
        ? childValue
        : normalizeOpsImageData(childValue, childKey)
    ])
  )
}

export const normalizeOpsImageResponse = (response) => {
  if (
    !proxyEnabled ||
    !response ||
    !Object.prototype.hasOwnProperty.call(response, 'data')
  ) {
    return response
  }

  // Presign responses are part of the upload/business-data flow.
  // Keep the complete response untouched so upload destinations,
  // public URLs, keys, and fields remain exactly as returned by backend.
  const requestUrl = String(response?.config?.url || '')

  if (PRESIGN_REQUEST_PATTERN.test(requestUrl)) {
    return response
  }

  response.data = normalizeOpsImageData(response.data)

  return response
}
